import React, { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft, Users, Crown, Copy, Plus, Settings, MessageCircle,
  UserPlus, UserMinus, LogOut, Share2, Trophy, Globe, Lock, Calendar, Send, Zap,
} from 'lucide-react'
import clsx from 'clsx'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { Avatar } from '@/components/ui/Avatar'
import { PageLoader } from '@/components/ui/Loader'
import { useLeaguesStore } from '@/stores/leaguesStore'
import { useAuthStore } from '@/stores/authStore'
import { leaguesApi } from '@/services/api'
import { supabase } from '@/services/supabase'
import toast from 'react-hot-toast'
import LiveFeed from '@/components/leagues/LiveFeed'
import { useScoreToast } from '@/hooks/useScoreToast'
import { TeamFlairBadge } from '@/components/ui/TeamFlairBadge'
import { UserProfileModal } from '@/components/ui/UserProfileModal'

/* ── Glass primitives ───────────────────────────────────── */
const GlassCard: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className, ...props }) => (
  <div className={clsx('backdrop-blur-xl bg-white/[0.04] border border-white/[0.08] rounded-2xl shadow-lg', className)} {...props} />
)

const LeagueDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const {
    current: leagueDetail,
    isLoading, error, fetchLeague, leaveLeague,
  } = useLeaguesStore()

  const [activeTab, setActiveTab] = useState<'leaderboard' | 'live' | 'members' | 'chat'>('leaderboard')
  const [newMessage, setNewMessage] = useState('')
  const [messages, setMessages] = useState<any[]>([])
  const [chatLoading, setChatLoading] = useState(false)
  const chatEndRef = useRef<HTMLDivElement>(null)

  const currentLeague = leagueDetail?.league
  const members = leagueDetail?.members ?? []

  const currentUserId = useAuthStore((s) => s.user?.id) ?? null
  const isCommissioner = leagueDetail?.league.role === 'owner' || leagueDetail?.league.role === 'admin'

  // Fire toast when user's score increases
  const myMember = members.find((m) => m.userId === currentUserId)
  useScoreToast(myMember?.score ?? null)

  const [invites, setInvites] = useState<any[]>([])
  const [invitesLoading, setInvitesLoading] = useState(false)
  const [showScoringSettings, setShowScoringSettings] = useState(false)
  const [viewProfileId, setViewProfileId] = useState<string | null>(null)
  const [scoringSettings, setScoringSettings] = useState({
    useUpsetBonus: true, useStreakBonus: true, useConfidencePoints: false,
  })
  const [savingScoringSettings, setSavingScoringSettings] = useState(false)
  const [recomputingScores, setRecomputingScores] = useState(false)

  // Prize pool local state
  const [prizePoolStr, setPrizePoolStr] = useState<string>('0')
  const [prizeNotes, setPrizeNotes] = useState<string>('')
  const [prizeDirty, setPrizeDirty] = useState(false)

  useEffect(() => { if (id) fetchLeague(id) }, [id, fetchLeague])

  // Sync prize fields from server
  useEffect(() => {
    if (currentLeague) {
      setPrizePoolStr(String(currentLeague.prizePool ?? 0))
      setPrizeNotes(currentLeague.prizeNotes ?? '')
      setPrizeDirty(false)
    }
  }, [currentLeague?.prizePool, currentLeague?.prizeNotes])

  useEffect(() => {
    if (!id || !isCommissioner) return
    let mounted = true
    setInvitesLoading(true)
    leaguesApi.invitesList(id).then((res) => { if (mounted) setInvites(res.invites ?? []) }).catch(() => {}).finally(() => { if (mounted) setInvitesLoading(false) })
    leaguesApi.getScoringSettings(id).then((res) => {
      if (!mounted) return
      setScoringSettings({
        useUpsetBonus: !!res?.settings?.useUpsetBonus,
        useStreakBonus: !!res?.settings?.useStreakBonus,
        useConfidencePoints: !!res?.settings?.useConfidencePoints,
      })
    }).catch(() => {})
    return () => { mounted = false }
  }, [id, isCommissioner])

  /* ── Chat loading + realtime ────────────────────────────── */
  useEffect(() => {
    if (!id) return
    let mounted = true
    setChatLoading(true)

    ;(async () => {
      try {
        const res = await leaguesApi.getMessages(id)
        if (!mounted) return
        const msgs = (res?.messages || []).map((m: any) => ({
          id: m.id,
          userId: m.userId,
          username: m.displayName || 'Unknown',
          avatarUrl: null,
          favoriteTeamId: m.favoriteTeamId || null,
          body: m.body,
          createdAt: m.createdAt,
        }))
        setMessages(msgs)
      } catch (e) {
        console.warn('Chat load error', e)
      } finally {
        if (mounted) setChatLoading(false)
      }
    })()

    const channel = supabase
      .channel(`league-chat-${id}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'league_messages', filter: `league_id=eq.${id}` },
        async (payload: any) => {
          const msg = payload.new
          const { data: profile } = await supabase.from('user_profiles').select('id, display_name, avatar_url, favorite_team_id').eq('id', msg.user_id).maybeSingle()
          setMessages((prev) => {
            if (prev.some((m) => m.id === msg.id)) return prev
            return [...prev, { id: msg.id, userId: msg.user_id, username: profile?.display_name || 'Unknown', avatarUrl: profile?.avatar_url || null, favoriteTeamId: profile?.favorite_team_id || null, body: msg.body, createdAt: msg.created_at }]
          })
        }
      ).subscribe()

    return () => { mounted = false; supabase.removeChannel(channel) }
  }, [id])

  // Auto-scroll chat
  useEffect(() => {
    if (activeTab === 'chat') chatEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, activeTab])

  /* ── Handlers ───────────────────────────────────────────── */
  const handleCopyInviteCode = async () => {
    const code = leagueDetail?.league.inviteCode
    if (!code) return
    try { await navigator.clipboard.writeText(code); toast.success('Invite code copied!') } catch { toast.error('Failed to copy') }
  }

  const handleCopyInviteLink = async () => {
    const code = leagueDetail?.league.inviteCode
    if (!code) return
    try { await navigator.clipboard.writeText(`${window.location.origin}/join/${code}`); toast.success('Invite link copied!') } catch { toast.error('Failed to copy') }
  }

  const handleLeaveLeague = async () => {
    const lid = leagueDetail?.league.id
    if (!lid || !window.confirm('Leave this league?')) return
    try { await leaveLeague(lid); toast.success('Left league'); navigate('/leagues') } catch { toast.error('Failed to leave league') }
  }

  const handleSendMessage = async () => {
    if (!id || !currentUserId) return
    const body = newMessage.trim()
    if (!body) return
    try {
      await leaguesApi.sendMessage(id, body)
      setNewMessage('')
    } catch (e: any) { toast.error(e?.message ?? 'Failed to send message') }
  }

  const handleSavePrize = async () => {
    if (!id) return
    try {
      await leaguesApi.update(id, { prizePool: parseInt(prizePoolStr) || 0, prizeNotes })
      setPrizeDirty(false)
      toast.success('Prize pool saved')
    } catch { toast.error('Failed to save prize pool') }
  }

  const formatTimeAgo = (dateLike: Date | string): string => {
    const date = dateLike instanceof Date ? dateLike : new Date(dateLike)
    const diff = Date.now() - date.getTime()
    const mins = Math.floor(diff / 60000)
    const hrs = Math.floor(diff / 3600000)
    if (mins < 1) return 'just now'
    if (mins < 60) return `${mins}m ago`
    if (hrs < 24) return `${hrs}h ago`
    return date.toLocaleDateString()
  }

  const formatTime = (dateLike: string) => {
    const d = new Date(dateLike)
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }

  const scoringModeBadges = [
    { key: 'basic', label: 'Basic' },
    ...(scoringSettings.useUpsetBonus ? [{ key: 'upset', label: 'Upset' }] : []),
    ...(scoringSettings.useStreakBonus ? [{ key: 'streak', label: 'Streak' }] : []),
  ]

  if (isLoading) return <PageLoader text="Loading league details..." />

  if (error || !currentLeague) {
    return (
      <div className="min-h-screen bg-transparent flex items-center justify-center p-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-4">League Not Found</h2>
          <p className="text-white/30 mb-6">{error || 'This league does not exist or you do not have access.'}</p>
          <Button onClick={() => navigate('/leagues')}>Back to Leagues</Button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-transparent p-4 lg:p-6">
      <div className="max-w-6xl mx-auto">
        {/* Back */}
        <Button variant="ghost" onClick={() => navigate('/leagues')} leftIcon={<ArrowLeft className="h-4 w-4" />} className="mb-5 text-white/40 hover:text-white/70">
          Back to Leagues
        </Button>

        {/* ── League Header ──────────────────────────────── */}
        <GlassCard className="p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <h1 className="text-3xl font-bold text-white">{currentLeague.name}</h1>
                {currentLeague.privacy === 'public' ? <Globe className="h-5 w-5 text-blue-400/60" /> : <Lock className="h-5 w-5 text-yellow-400/60" />}
              </div>
              {currentLeague.description && <p className="text-white/40 text-sm mb-3">{currentLeague.description}</p>}
              <div className="flex flex-wrap gap-4 text-xs text-white/25">
                <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" />{members.length}/{currentLeague.maxMembers} members</span>
                <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />Created {new Date(currentLeague.createdAt as any).toLocaleDateString()}</span>
                {currentLeague.isOwner && <span>Created by you</span>}
              </div>

              {/* Prize Pool Display */}
              {((currentLeague.prizePool ?? 0) > 0 || currentLeague.prizeNotes) && (
                <div className="mt-3 p-3 bg-amber-500/[0.06] border border-amber-500/20 rounded-xl">
                  <div className="flex items-center gap-2 mb-1">
                    <Trophy className="h-4 w-4 text-amber-400" />
                    <span className="text-sm font-medium text-amber-400">Prize Pool{(currentLeague.prizePool ?? 0) > 0 ? `: $${currentLeague.prizePool}` : ''}</span>
                  </div>
                  {currentLeague.prizeNotes && <p className="text-xs text-amber-400/60">{currentLeague.prizeNotes}</p>}
                  <p className="text-[10px] text-white/15 mt-1">Organizer-managed. BracketBlitz does not process payouts.</p>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex flex-col gap-3 min-w-[200px]">
              {isCommissioner && currentLeague.inviteCode && (
                <div className="p-3 bg-white/[0.03] border border-white/[0.06] rounded-xl">
                  <div className="text-[10px] text-white/20 mb-1">Invite Code</div>
                  <div className="flex items-center gap-2">
                    <code className="font-mono text-orange-400 font-bold text-sm">{currentLeague.inviteCode}</code>
                    <button onClick={handleCopyInviteCode} className="text-white/20 hover:text-white/50 transition-colors"><Copy className="h-4 w-4" /></button>
                  </div>
                </div>
              )}
              <Button variant="outline" onClick={handleCopyInviteLink} leftIcon={<Share2 className="h-4 w-4" />} size="sm">Share Invite Link</Button>
              {isCommissioner && <Button variant="outline" leftIcon={<Settings className="h-4 w-4" />} size="sm" onClick={() => setShowScoringSettings(true)}>League Settings</Button>}
              {!isCommissioner && (
                <Button variant="outline" onClick={handleLeaveLeague} leftIcon={<LogOut className="h-4 w-4" />} size="sm" className="border-red-400/20 text-red-400 hover:bg-red-400/10">Leave League</Button>
              )}
            </div>
          </div>
        </GlassCard>

        {/* ── Pending Invites ────────────────────────────── */}
        {isCommissioner && (
          <GlassCard className="p-5 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-white flex items-center gap-2"><UserPlus className="h-4 w-4 text-orange-400" /> Pending Invites</h3>
              <Button size="sm" variant="primary" onClick={async () => {
                if (!id) return
                try { const res = await leaguesApi.invitesCreate(id); toast.success('Invite created'); setInvites((prev) => [res.invite, ...prev]) }
                catch (e: any) { toast.error(e?.message ?? 'Failed') }
              }} disabled={invitesLoading} leftIcon={<Plus className="h-3.5 w-3.5" />}>Create</Button>
            </div>
            {invitesLoading ? (
              <div className="text-white/20 text-xs">Loading invites…</div>
            ) : invites.filter((i) => i.status === 'pending').length === 0 ? (
              <div className="text-white/20 text-xs italic">No pending invites.</div>
            ) : (
              <div className="space-y-2">
                {invites.filter((i) => i.status === 'pending').map((invite) => (
                  <div key={invite.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 rounded-xl bg-white/[0.02] border border-white/[0.06]">
                    <div className="min-w-0">
                      <div className="text-white font-mono text-xs break-all">{invite.token}</div>
                      <div className="text-[10px] text-white/15 mt-0.5">Created {new Date(invite.createdAt).toLocaleString()}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="primary" onClick={async () => { try { await navigator.clipboard.writeText(`${window.location.origin}/join/${invite.token}`); toast.success('Invite link copied') } catch { toast.error('Failed') } }} leftIcon={<Share2 className="h-3.5 w-3.5" />}>Copy Link</Button>
                      <Button size="sm" variant="outline" onClick={async () => { try { await navigator.clipboard.writeText(invite.token); toast.success('Code copied') } catch { toast.error('Failed') } }} leftIcon={<Copy className="h-3.5 w-3.5" />}>Code</Button>
                      <Button size="sm" variant="outline" className="border-red-400/20 text-red-400 hover:bg-red-400/10" onClick={async () => {
                        if (!id) return
                        try { await leaguesApi.invitesRevoke(id, invite.id); setInvites((prev) => prev.map((p) => p.id === invite.id ? { ...p, status: 'revoked' } : p)); toast.success('Revoked') }
                        catch (e: any) { toast.error(e?.message ?? 'Failed') }
                      }}>Revoke</Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </GlassCard>
        )}

        {/* ── Tab bar ─────────────────────────────────────── */}
        <div className="flex gap-1 mb-6">
          {(['leaderboard', 'live', 'members', 'chat'] as const).map((tab) => {
            const icons = { leaderboard: Trophy, live: Zap, members: Users, chat: MessageCircle }
            const Icon = icons[tab]
            const labels = { leaderboard: 'Standings', live: 'Live Feed', members: 'Members', chat: 'Chat' }
            return (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={clsx(
                  'px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200',
                  activeTab === tab
                    ? 'bg-white/[0.1] text-white backdrop-blur-xl border border-white/[0.12]'
                    : 'text-white/30 hover:text-white/50 hover:bg-white/[0.03]',
                )}
              >
                <Icon className="h-4 w-4 inline mr-1.5 -mt-0.5" />
                {labels[tab]}
              </button>
            )
          })}
        </div>

        {/* ── LEADERBOARD ─────────────────────────────────── */}
        {activeTab === 'leaderboard' && (
          <GlassCard className="overflow-hidden">
            <div className="p-5 border-b border-white/[0.06] flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                <Trophy className="h-4 w-4 text-orange-400" /> League Standings
              </h3>
              <div className="flex items-center gap-1.5 flex-wrap">
                {scoringModeBadges.map((m) => (
                  <span key={m.key} className="text-[10px] px-2 py-0.5 rounded-full bg-white/[0.06] text-white/40 border border-white/[0.08]">{m.label}</span>
                ))}
              </div>
            </div>
            {members.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/[0.06]">
                      <th className="text-left p-4 text-[11px] text-white/25 font-medium uppercase tracking-wider">Rank</th>
                      <th className="text-left p-4 text-[11px] text-white/25 font-medium uppercase tracking-wider">Player</th>
                      <th className="text-right p-4 text-[11px] text-white/25 font-medium uppercase tracking-wider">Points</th>
                      <th className="text-right p-4 text-[11px] text-white/25 font-medium uppercase tracking-wider">Correct</th>
                      <th className="text-right p-4 text-[11px] text-white/25 font-medium uppercase tracking-wider">Streak</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.map((member, index) => (
                      <tr
                        key={member.userId}
                        onClick={() => setViewProfileId(member.userId)}
                        className={clsx(
                          'border-b border-white/[0.04] last:border-b-0 hover:bg-white/[0.02] transition-colors cursor-pointer',
                          member.userId === currentUserId && 'bg-orange-500/[0.06]',
                        )}
                      >
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            {(member.rank ?? index + 1) <= 3 && (
                              <div className={clsx(
                                'w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold',
                                (member.rank ?? index + 1) === 1 && 'bg-yellow-400/20 text-yellow-400',
                                (member.rank ?? index + 1) === 2 && 'bg-gray-300/20 text-gray-300',
                                (member.rank ?? index + 1) === 3 && 'bg-amber-600/20 text-amber-600',
                              )}>
                                {member.rank ?? index + 1}
                              </div>
                            )}
                            {(member.rank ?? index + 1) > 3 && (
                              <span className="text-sm text-white/40 font-medium pl-1">#{member.rank ?? index + 1}</span>
                            )}
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <Avatar src={member.avatarUrl ?? undefined} alt={member.displayName ?? member.username} fallback={member.displayName ?? member.username} size="sm" />
                            <div>
                              <div className="font-medium text-white text-sm flex items-center gap-2">
                                <TeamFlairBadge teamId={(member as any).favoriteTeamId} size="xs" />
                                {member.displayName ?? member.username}
                                {member.role !== 'member' && <Crown className="h-3 w-3 text-yellow-400/60" />}
                                {member.userId === currentUserId && <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-500/20 text-orange-400">You</span>}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="p-4 text-right">
                          <span className="font-bold text-white text-sm">{(member.score ?? 0).toLocaleString()}</span>
                        </td>
                        <td className="p-4 text-right">
                          <span className="text-green-400/80 text-sm font-medium">
                            {member.correctPicks != null ? member.correctPicks : 0}
                            {member.totalPicks != null && member.totalPicks > 0 && (
                              <span className="text-white/15 text-xs">/{member.totalPicks}</span>
                            )}
                          </span>
                        </td>
                        <td className="p-4 text-right">
                          <span className="text-white/50 text-sm font-medium">{member.streak ?? 0}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-12 text-center">
                <Trophy className="h-10 w-10 text-white/10 mx-auto mb-3" />
                <h3 className="font-semibold text-white text-sm mb-1">No standings yet</h3>
                <p className="text-white/25 text-xs">Standings appear once games are scored.</p>
              </div>
            )}
          </GlassCard>
        )}

        {/* ── LIVE FEED ─────────────────────────────────── */}
        {activeTab === 'live' && (
          <GlassCard className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <Zap className="h-4 w-4 text-orange-400" />
              <span className="text-sm font-semibold text-white">Live Feed</span>
              <span className="text-[10px] text-white/20 ml-auto">Updates as games finish</span>
            </div>
            <LiveFeed leagueId={id} />
          </GlassCard>
        )}

        {/* ── MEMBERS ─────────────────────────────────────── */}
        {activeTab === 'members' && (
          <GlassCard className="p-5">
            <h3 className="text-sm font-semibold text-white flex items-center gap-2 mb-4">
              <Users className="h-4 w-4 text-orange-400" /> League Members ({members.length})
            </h3>
            {members.length > 0 ? (
              <div className="space-y-2">
                {members.map((member) => (
                  <div key={member.userId} className="flex items-center justify-between p-3 bg-white/[0.02] border border-white/[0.06] rounded-xl hover:bg-white/[0.04] transition-colors">
                    <div className="flex items-center gap-3">
                      <Avatar src={member.avatarUrl ?? undefined} alt={member.displayName ?? member.username} fallback={member.displayName ?? member.username} size="md" />
                      <div>
                        <div className="font-medium text-white text-sm flex items-center gap-2">
                          <TeamFlairBadge teamId={(member as any).favoriteTeamId} size="xs" />
                          {member.displayName ?? member.username}
                          {member.role !== 'member' && <Crown className="h-3.5 w-3.5 text-yellow-400/60" />}
                          {member.userId === currentUserId && <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-500/20 text-orange-400">You</span>}
                        </div>
                        <div className="text-[11px] text-white/20">Joined {new Date(member.joinedAt as any).toLocaleDateString()}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <div className="text-[10px] text-white/20">Rank</div>
                        <div className="font-bold text-orange-400 text-sm">#{member.rank}</div>
                      </div>
                      {isCommissioner && member.userId !== currentUserId && member.role === 'member' && (
                        <Button variant="outline" size="sm" onClick={async () => {
                          if (!id || !window.confirm(`Remove ${member.displayName ?? member.username}?`)) return
                          try { await leaguesApi.removeMember(id, member.userId); toast.success('Removed'); await fetchLeague(id) }
                          catch (e: any) { toast.error(e?.message ?? 'Failed') }
                        }} leftIcon={<UserMinus className="h-3.5 w-3.5" />} className="border-red-400/20 text-red-400 hover:bg-red-400/10">Remove</Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Users className="h-10 w-10 text-white/10 mx-auto mb-3" />
                <h3 className="font-semibold text-white text-sm mb-1">No members</h3>
                <p className="text-white/25 text-xs">Share your invite code to get people in.</p>
              </div>
            )}
          </GlassCard>
        )}

        {/* ── CHAT ────────────────────────────────────────── */}
        {activeTab === 'chat' && (
          <GlassCard className="flex flex-col" style={{ height: 'calc(100dvh - 340px)', minHeight: 400, paddingBottom: 'env(safe-area-inset-bottom, 0px)', marginBottom: '80px' }}>
            {/* Chat header */}
            <div className="px-5 py-3 border-b border-white/[0.06] flex items-center gap-2">
              <MessageCircle className="h-4 w-4 text-orange-400" />
              <span className="text-sm font-semibold text-white">League Chat</span>
              <span className="text-[10px] text-white/20 ml-auto">{messages.length} messages</span>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-5 py-3 space-y-0.5">
              {chatLoading ? (
                <div className="text-xs text-white/20 text-center py-12">Loading messages…</div>
              ) : messages.length === 0 ? (
                <div className="text-center py-12">
                  <MessageCircle className="h-8 w-8 text-white/10 mx-auto mb-2" />
                  <p className="text-xs text-white/20">No messages yet. Start the conversation! 💬</p>
                </div>
              ) : (
                messages.map((message, idx) => {
                  const prevMsg = idx > 0 ? messages[idx - 1] : null
                  const sameUser = prevMsg && prevMsg.userId === message.userId
                  const timeDiff = prevMsg ? new Date(message.createdAt).getTime() - new Date(prevMsg.createdAt).getTime() : Infinity
                  const grouped = sameUser && timeDiff < 120000
                  const isMe = message.userId === currentUserId

                  // Date separator
                  const showDate = !prevMsg || new Date(message.createdAt).toDateString() !== new Date(prevMsg.createdAt).toDateString()

                  return (
                    <React.Fragment key={message.id}>
                      {showDate && (
                        <div className="flex items-center gap-3 py-3">
                          <div className="flex-1 h-px bg-white/[0.06]" />
                          <span className="text-[10px] text-white/20 font-medium">{new Date(message.createdAt).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                          <div className="flex-1 h-px bg-white/[0.06]" />
                        </div>
                      )}
                      <div className={clsx('flex items-start gap-2.5', grouped ? 'mt-0.5' : 'mt-3 first:mt-0')}>
                        {grouped ? (
                          <div className="w-8 shrink-0" />
                        ) : (
                          <Avatar src={message.avatarUrl ?? undefined} alt={message.username} fallback={message.username} size="sm" />
                        )}
                        <div className="flex-1 min-w-0">
                          {!grouped && (
                            <div className="flex items-center gap-2 mb-0.5">
                              <TeamFlairBadge teamId={(message as any).favoriteTeamId} size="xs" />
                              <span onClick={(e) => { e.stopPropagation(); setViewProfileId(message.userId) }} className={clsx('text-xs font-medium cursor-pointer hover:underline', isMe ? 'text-orange-400' : 'text-white/70')}>{message.username}</span>
                              <span className="text-[10px] text-white/15">{formatTime(message.createdAt)}</span>
                            </div>
                          )}
                          <p className="text-sm text-white/60 break-words leading-relaxed">{message.body}</p>
                        </div>
                      </div>
                    </React.Fragment>
                  )
                })
              )}
              <div ref={chatEndRef} />
            </div>

            {/* Composer */}
            <div className="px-4 py-3 border-t border-white/[0.06]">
              <div className="flex gap-2 items-center">
                <input
                  placeholder="Type a message..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                  className="flex-1 px-4 py-2.5 bg-white/[0.04] border border-white/[0.08] rounded-xl text-sm text-white placeholder-white/20 focus:outline-none focus:border-white/20 transition-colors"
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!newMessage.trim() || !currentUserId}
                  className={clsx(
                    'p-2.5 rounded-xl transition-all duration-200',
                    newMessage.trim() ? 'bg-orange-500 text-white hover:bg-orange-400' : 'bg-white/[0.04] text-white/20',
                  )}
                >
                  <Send className="h-4 w-4" />
                </button>
              </div>
            </div>
          </GlassCard>
        )}

        {/* ── Settings Modal ──────────────────────────────── */}
        {showScoringSettings && isCommissioner && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <GlassCard className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-white/[0.06] flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-white">League Settings</h3>
                  <p className="text-xs text-white/25 mt-1">Scoring rules + prize pool</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setShowScoringSettings(false)}>Close</Button>
              </div>

              <div className="p-6 space-y-4">
                {/* Scoring toggles */}
                {[
                  { key: 'useUpsetBonus', label: 'Upset Bonus', desc: 'Extra points for correct lower-seed upsets', enabled: true },
                  { key: 'useStreakBonus', label: 'Streak Bonus', desc: 'Per-round streak bonus for consecutive correct picks', enabled: true },
                  { key: 'useConfidencePoints', label: 'Confidence Points', desc: 'Advanced mode (coming soon)', enabled: false },
                ].map((item) => (
                  <label key={item.key} className={clsx('flex items-center justify-between gap-3 p-3 rounded-xl bg-white/[0.02] border border-white/[0.06]', !item.enabled && 'opacity-40')}>
                    <div>
                      <div className="text-white text-sm font-medium">{item.label}</div>
                      <div className="text-[11px] text-white/25">{item.desc}</div>
                    </div>
                    <input
                      type="checkbox"
                      checked={item.enabled ? (scoringSettings as any)[item.key] : false}
                      disabled={!item.enabled}
                      onChange={(e) => item.enabled && setScoringSettings((s) => ({ ...s, [item.key]: e.target.checked }))}
                      className="accent-orange-500"
                    />
                  </label>
                ))}

                <div className="p-3 rounded-xl bg-orange-500/[0.06] border border-orange-500/20 text-xs text-orange-200/60">
                  <strong>Rule preview:</strong> Basic round weights{scoringSettings.useUpsetBonus ? ' + upset bonus' : ''}{scoringSettings.useStreakBonus ? ' + streak bonus' : ''}
                </div>

                {/* Prize Pool */}
                <div className="pt-4 mt-4 border-t border-white/[0.06]">
                  <div className="flex items-center gap-2 mb-3">
                    <Trophy className="h-4 w-4 text-amber-400" />
                    <span className="text-sm font-medium text-white">Prize Pool (Optional)</span>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <label className="text-[10px] text-white/25 mb-1 block">Amount ($)</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={prizePoolStr}
                        onChange={(e) => { setPrizePoolStr(e.target.value.replace(/[^0-9]/g, '')); setPrizeDirty(true) }}
                        onBlur={() => setPrizePoolStr(String(parseInt(prizePoolStr) || 0))}
                        className="w-full px-3 py-2 bg-white/[0.04] border border-white/[0.08] rounded-lg text-sm text-white focus:outline-none focus:border-white/20"
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-white/25 mb-1 block">Prize Notes</label>
                      <input
                        type="text"
                        value={prizeNotes}
                        onChange={(e) => { setPrizeNotes(e.target.value); setPrizeDirty(true) }}
                        className="w-full px-3 py-2 bg-white/[0.04] border border-white/[0.08] rounded-lg text-sm text-white focus:outline-none focus:border-white/20"
                        placeholder="e.g. Winner gets bragging rights + $50"
                      />
                    </div>
                    <p className="text-[10px] text-white/15">BracketBlitz does not collect, escrow, or distribute money. Organizer-managed only.</p>
                  </div>
                </div>
              </div>

              <div className="p-6 border-t border-white/[0.06] flex flex-wrap justify-end gap-2">
                <Button variant="outline" onClick={() => setShowScoringSettings(false)} disabled={savingScoringSettings || recomputingScores}>Cancel</Button>
                <Button variant="outline" disabled={savingScoringSettings || recomputingScores || !id}
                  onClick={async () => {
                    if (!id) return
                    setSavingScoringSettings(true)
                    try {
                      if (prizeDirty) await leaguesApi.update(id, { prizePool: parseInt(prizePoolStr) || 0, prizeNotes })
                      await leaguesApi.updateScoringSettings(id, { useUpsetBonus: scoringSettings.useUpsetBonus, useStreakBonus: scoringSettings.useStreakBonus, useConfidencePoints: false })
                      setPrizeDirty(false)
                      toast.success('Settings saved')
                      setShowScoringSettings(false)
                    } catch (e: any) { toast.error(e?.message ?? 'Failed') }
                    finally { setSavingScoringSettings(false) }
                  }}
                >{savingScoringSettings ? 'Saving…' : 'Save Settings'}</Button>
                <Button variant="primary" disabled={savingScoringSettings || recomputingScores || !id}
                  onClick={async () => {
                    if (!id) return
                    setSavingScoringSettings(true)
                    try {
                      if (prizeDirty) await leaguesApi.update(id, { prizePool: parseInt(prizePoolStr) || 0, prizeNotes })
                      await leaguesApi.updateScoringSettings(id, { useUpsetBonus: scoringSettings.useUpsetBonus, useStreakBonus: scoringSettings.useStreakBonus, useConfidencePoints: false })
                      setPrizeDirty(false)
                    } catch (e: any) { toast.error(e?.message ?? 'Failed'); return } finally { setSavingScoringSettings(false) }
                    setRecomputingScores(true)
                    try {
                      await leaguesApi.recomputeScores(id)
                      await fetchLeague(id)
                      toast.success('Settings saved + scores recomputed')
                      setShowScoringSettings(false)
                    } catch (e: any) { toast.error(e?.message ?? 'Recompute failed') }
                    finally { setRecomputingScores(false) }
                  }}
                >{recomputingScores ? 'Recomputing…' : 'Save + Recompute'}</Button>
              </div>
            </GlassCard>
          </div>
        )}
      </div>
    <UserProfileModal userId={viewProfileId} onClose={() => setViewProfileId(null)} />
</div>
  )
}

export { LeagueDetailPage }
